/**
 * Carrying a referral code across the sign-up it triggers.
 *
 * The friend arrives on an invite link with no account. Everything the referral
 * needs — a Supabase user id — only exists after they sign up, and sign-up is not
 * one hop: `/login` round-trips a return path, but a NEW user is then sent to
 * `/onboarding/0` by ProtectedRoute, and that redirect discards the return path.
 * So the one audience referrals exist for is exactly the audience the built-in
 * round-trip loses.
 *
 * Hence a stash. The code is written down when the invite page is opened and
 * survives login, onboarding, a reload, and a cold start; the redirect decision
 * below brings the friend back to finish once they are a real, onboarded user.
 *
 * Deliberately localStorage and not React state: the onboarding flow remounts the
 * tree and the app can be killed and relaunched mid-signup on native.
 */

const KEY = "sy_pending_referral";

/** How long a stashed code stays valid. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Mirrors the `code` bound in redeem-referral's request schema. */
const MAX_CODE_LENGTH = 64;

export interface PendingReferral {
  code: string;
  /** Epoch ms, so an abandoned code cannot ambush someone weeks later. */
  storedAt: number;
}

/**
 * Every accessor is wrapped: Safari private mode throws on localStorage access
 * rather than returning null, and a referral is never worth crashing a page over.
 */
export function stashReferralCode(code: string, now: number = Date.now()): void {
  try {
    // Bounded because the code comes straight off a URL anyone can craft, and a
    // stashed value is replayed on every launch for a week. 64 matches the
    // edge function's own Zod limit.
    if (!code || code.length > MAX_CODE_LENGTH) return;
    // Re-stashing the SAME code keeps the original timestamp. The invite page
    // writes on every mount and `decidePendingReferralRedirect` sends the friend
    // back to that page, so refreshing `storedAt` here would roll the 7-day TTL
    // forward on every app launch — and a code that keeps failing retryably (a
    // truncated shared link, say) is never cleared, so it would bounce them to
    // the invite screen on every cold start, forever. Only a NEW code starts a
    // new clock.
    if (readPendingReferral(now)?.code === code) return;
    localStorage.setItem(KEY, JSON.stringify({ code, storedAt: now } satisfies PendingReferral));
  } catch {
    /* no stash — the friend can still redeem in this session, just not after a signup hop */
  }
}

export function readPendingReferral(now: number = Date.now()): PendingReferral | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingReferral>;
    if (typeof parsed?.code !== "string" || !parsed.code) return null;
    if (parsed.code.length > MAX_CODE_LENGTH) return null;
    if (typeof parsed.storedAt !== "number" || !Number.isFinite(parsed.storedAt)) return null;
    // A future timestamp means a tampered or clock-skewed stash; treat it as
    // expired rather than trusting it forever.
    if (parsed.storedAt > now + 60_000) return null;
    if (now - parsed.storedAt > TTL_MS) {
      clearPendingReferral();
      return null;
    }
    return { code: parsed.code, storedAt: parsed.storedAt };
  } catch {
    return null;
  }
}

export function clearPendingReferral(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export interface PendingRedirectInput {
  /** The stashed code, or null when there is none. */
  pendingCode: string | null;
  /** Whether the referral program is switched on at all. */
  enabled: boolean;
  isAuthenticated: boolean;
  hasCompletedOnboarding: boolean;
  /** `location.pathname` right now. */
  currentPath: string;
}

/**
 * Where a signed-in, onboarded user with a stashed code should be sent.
 *
 * Returns null far more often than it returns a path, and that is the point: this
 * runs on every navigation, so every reason NOT to redirect has to be explicit or
 * it becomes a loop. In particular it never fires while the friend is still
 * mid-signup, and never on an invite page — theirs or a newer one.
 */
export function decidePendingReferralRedirect(input: PendingRedirectInput): string | null {
  const { pendingCode, enabled, isAuthenticated, hasCompletedOnboarding } = input;
  // React Router matches paths case-insensitively, so /Invite/X IS the invite
  // page. Every prefix check below has to agree with the router about that.
  const currentPath = input.currentPath.toLowerCase();

  if (!enabled) return null;
  if (!pendingCode) return null;
  // Mid-signup. Redirecting here would yank them out of onboarding and, because
  // the invite page would bounce them straight back, spin.
  if (!isAuthenticated) return null;
  if (!hasCompletedOnboarding) return null;

  // Any invite page — not just the stashed one. Standing on the target is the
  // self-redirect loop; standing on a DIFFERENT invite is worse: InviteLanding is
  // lazy-loaded, so this runs before that page mounts and re-stashes its own
  // code, and redirecting would send the friend to the OLD code — rewarding the
  // wrong member. The link they just opened always wins; it re-stashes itself.
  if (currentPath.startsWith("/invite/")) return null;

  // Auth screens are transient states on the way somewhere else; stepping in
  // front of them races the navigation they are already performing.
  if (currentPath.startsWith("/login") || currentPath.startsWith("/onboarding")) return null;

  // The manual code screen is a DELIBERATE act — someone standing on it is
  // typing a code right now. Redirecting them to a stashed one strands anybody
  // whose link carried a bad code: the stash survives a retryable failure, so
  // they would be bounced back to the code that already failed, every time,
  // with no way to reach the form that would let them fix it.
  if (currentPath.startsWith("/redeem-referral")) return null;

  return `/invite/${encodeURIComponent(pendingCode)}`;
}
